import {
  Box,
  Table,
  TableContainer,
  Text,
  useDisclosure,
  useToken,
  Flex,
} from "@chakra-ui/react";
import React, { Fragment, useState, useEffect, useMemo } from "react";
import PrimaryButton from "../../components/themeComponents/PrimaryButton";
import CampaignDraftDrawer from "./CampaignDraftDrawer";
import DateRangeSelection from "../../components/DateRangeSelection";
import moment from "moment";
import TableHead from "./components/TableHead";
import CampaignTable from "./components/CampaignTable";
import { useQuery } from "react-query";
import { getBroadcastHeaders, getCampaigns } from "../../Services/api";
import { prepareCampaignListData } from "../../_helpers/data_helper";
import CampaignFilter from "./components/CampaignFilter";
import { connect } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import hexToRgba from "hex-to-rgba";
import { OvalSpinner } from "../../components/Loaders";
import OutlinedButton from "../../components/themeComponents/OutlinedButton";
import DownloadReportModal from "./DownloadReportModal";
import Pagination from "../../components/Pagination";

function CampaignList({ org }) {
  const [mainColor] = useToken("colors", ["primary.main"]);
  const navigate = useNavigate();
  const location = useLocation();

  // ────────────────────────────────────────────────
  // Parse initial state from URL query params
  // ────────────────────────────────────────────────
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const getInitialDateRange = () => {
    const start = searchParams.get("startDate");
    const end = searchParams.get("endDate");

    if (start && end && moment(start).isValid() && moment(end).isValid()) {
      return {
        startDate: moment(start),
        endDate: moment(end),
      };
    }
    return {
      startDate: moment().subtract(30, "days"),
      endDate: moment(),
    };
  };

  const normalizeDateRange = (range) => ({
    startDate: moment(range.startDate),
    endDate: moment(range.endDate),
  });

  const getInitialFilter = () => ({
    campaignName: searchParams.get("campaignName") || "",
    channel: searchParams.get("channel") || "Email",
    status: searchParams.get("status") || "ALL",
  });

  const getInitialPage = () => {
    const page = parseInt(searchParams.get("page"), 10);
    return isNaN(page) || page < 1 ? 1 : page;
  };

  const [dateRange, setDateRange] = useState(getInitialDateRange);
  const [campaignFilter, setCampaignFilter] = useState(getInitialFilter);
  const [currentPage, setCurrentPage] = useState(getInitialPage);
  const [totalCount, setTotalCount] = useState(0);
  const [broadcastHeaders, setBroadcastHeaders] = useState({});
  const [isDownloadReportModalOpen, setIsDownloadReportModalOpen] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const pageSize = 5;

  // ────────────────────────────────────────────────
  // Update URL whenever filters / page / date change
  // ────────────────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams();

    // Date range (ISO format)
    params.set("startDate", dateRange.startDate.format("YYYY-MM-DD"));
    params.set("endDate", dateRange.endDate.format("YYYY-MM-DD"));

    // Filters
    if (campaignFilter.campaignName) {
      params.set("campaignName", campaignFilter.campaignName);
    }
    if (campaignFilter.channel && campaignFilter.channel !== "Email") {
      // only set if not default
      params.set("channel", campaignFilter.channel);
    }
    if (campaignFilter.status && campaignFilter.status !== "ALL") {
      params.set("status", campaignFilter.status);
    }

    // Page (only if > 1)
    if (currentPage > 1) {
      params.set("page", currentPage);
    }

    const newSearch = params.toString();
    const currentSearch = location.search.slice(1); // remove ?

    if (newSearch !== currentSearch) {
      navigate(
        {
          pathname: location.pathname,
          search: newSearch ? `?${newSearch}` : "",
        },
        { replace: true } // don't create new history entry on every change
      );
    }
  }, [dateRange, campaignFilter, currentPage, navigate, location.pathname, location.search]);

  // ────────────────────────────────────────────────
  // Data fetching
  // ────────────────────────────────────────────────
  const campaignQuery = useQuery(
    ["campaigns", org.token, dateRange, campaignFilter, currentPage],
    async ({ signal }) => {
      const abortController = new AbortController();
      signal.addEventListener("abort", () => abortController.abort());

      try {
        const [campaignsRes, headersRes] = await Promise.all([
          getCampaigns(
            org.token,
            campaignFilter,
            dateRange,
            currentPage,
            { signal: abortController.signal }
          ),
          getBroadcastHeaders({ signal: abortController.signal }),
        ]);

        setTotalCount(campaignsRes?.data?.count ?? 0);
        setBroadcastHeaders(headersRes.data);

        return prepareCampaignListData(
          campaignsRes.data.campaigns,
          headersRes.data,
          campaignFilter
        );
      } catch (error) {
        if (error.name !== "AbortError") {
          throw error;
        }
      }
    },
    {
      staleTime: 0,
      retry: 1,
      enabled: !!org.token,
      onError: (error) => {
        console.error("Error fetching campaigns:", error);
      },
    }
  );

  // ────────────────────────────────────────────────
  // Handlers
  // ────────────────────────────────────────────────
  const onDateChange = (newRange) => {
    setDateRange(normalizeDateRange(newRange));
    setCurrentPage(1);
  };

  const onFilterChange = (newFilterValues) => {
    setCampaignFilter((prev) => ({ ...prev, ...newFilterValues }));
    setCurrentPage(1);
  };

  const onPageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <Fragment>
      <DateRangeSelection
        dateRange={dateRange}
        onDateChange={onDateChange}
        isLoading={campaignQuery.isLoading}
      />

      <Box
        zIndex={99}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Text fontSize="1vw" as="b">
          Campaigns
        </Text>
        <OutlinedButton label="Create Campaign" onClick={onOpen} />
      </Box>

      <CampaignFilter
        isLoading={campaignQuery.isLoading}
        downloadReport={() => setIsDownloadReportModalOpen(true)}
        channels={broadcastHeaders}
        campaignFilter={campaignFilter}
        onFilter={onFilterChange}
      />

      <TableContainer
        bg="white"
        maxH="60vh"
        w="99%"
        overflowY="auto"
        mt={0}
        position="relative"
      >
        {campaignQuery.isLoading && <OvalSpinner />}

        <Table minH="250px" opacity={campaignQuery.isLoading ? 0.5 : 1}>
          {campaignQuery?.data?.data?.length > 0 ? (
            <Fragment>
              <TableHead headersProps={broadcastHeaders[campaignFilter.channel] || ["Campaign Name", "Status", "Performance", "Enagement", "Revenue", "Action"]} />
              <CampaignTable
                selectedChannel={campaignFilter.channel}
                campaignDataRaw={campaignQuery.data.data}
                campaignQuery={campaignQuery}
                broadcastHeaders={broadcastHeaders}
                campaignData={campaignQuery.data.data}
              />
            </Fragment>
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              py={10}
            >
              <Text fontSize="lg" fontWeight="bold" color="gray.600">
                No campaigns have been created yet.
              </Text>
            </Box>
          )}
        </Table>
      </TableContainer>

      {!campaignQuery.isLoading && campaignQuery?.data?.data?.length > 0 && (
        <Pagination
          totalCount={totalCount}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={onPageChange}
          isDisabled={campaignQuery.isLoading}
        />
      )}

      <CampaignDraftDrawer
        isOpen={isOpen}
        onClose={onClose}
        orgToken={org.token}
      />

      <DownloadReportModal
        dateRange={dateRange}
        channel={campaignFilter.channel}
        campaignFilter={campaignFilter}
        isOpen={isDownloadReportModalOpen}
        onClose={() => setIsDownloadReportModalOpen(false)}
      />
    </Fragment>
  );
}

const mapStateToProps = (state) => {
  return {
    ...state,
    org: state.auth.selectedOrg,
  };
};

export default connect(mapStateToProps)(CampaignList);
